import { useState, useEffect, useCallback, useRef } from 'react';
import { TapUser, TapStatus } from '@/domain/types';
import { tapApi } from '@/services/tapApi';

export interface UserState {
  user: TapUser | null;
  status: TapStatus | null;
  isLoading: boolean;
  error: string | null;
}

export interface UserActions {
  refreshUser: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

export function useTapUser(): [UserState, UserActions] {
  const [state, setState] = useState<UserState>({
    user: null,
    status: null,
    isLoading: true,
    error: null,
  });

  const fetchedRef = useRef(false);

  const refreshUser = useCallback(async () => {
    try {
      const user = await tapApi.getMe();
      setState((prev) => ({ ...prev, user, error: null }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to load user',
      }));
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const status = await tapApi.getStatus();
      setState((prev) => ({ ...prev, status, error: null }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to load status',
      }));
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const [user, status] = await Promise.all([
        tapApi.getMe(),
        tapApi.getStatus(),
      ]);
      setState({ user, status, isLoading: false, error: null });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load data',
      }));
    }
  }, []);

  // Initial load - prevent double fetch in StrictMode
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    console.log('[useTapUser] Starting initial load, initData length:', tapApi.getInitData()?.length);
    refreshAll();
  }, [refreshAll]);

  return [state, { refreshUser, refreshStatus, refreshAll }];
}
